/* eslint-disable import/first */
/* eslint-disable global-require */
import config from 'config'
import '@neocoast/semantic-ui-less/semantic.less'
import '@groovehq/internal-design-system/lib/styles/global/preflight/index.css'
import '@groovehq/internal-design-system/lib/styles/global/utility/index.css'
import 'whatwg-fetch'
// Polyfill
require('intersection-observer') // Polyfill - load before other modules
require('css.escape')

import store from 'store'
import startIntersectionObserver from 'util/intersectionObserver'
import sharedStorage from 'util/sharedStorage'
import storage from 'util/storage'
import { captureVersion } from 'util/version'
import { refreshOnNextPage } from 'util/refreshMiddleware'
import React from 'react'
import ReactDOM from 'react-dom'
import metrics from 'util/metrics'
import memory from 'util/memoryUsageMiddleware'
import * as salesforce from 'ducks/integrations/salesforce/api'
import { initializeTracking, trackPage } from 'util/tracking'
import { cacheRegistry } from 'util/memoization'
import { doShowSnackbar } from 'actions/snackbar'
import fpsExtension from 'util/fpsAppExtensions'
import { registerListeners as registerActionsLogListener } from 'util/performance_sim/listeners'
import * as agentInfo from 'util/agentInfo'
import * as mailboxInfo from 'util/mailboxInfo'
import { doRefreshCounts } from 'ducks/searches/operations/doRefreshCounts'

// Helper to check if current path is a login or public page
function isLoginOrPublicPage(currentPath) {
  // Extract pathname portion and normalize
  const pathname = currentPath.split('?')[0].split('#')[0]

  return (
    pathname.startsWith('/login') ||
    pathname.startsWith('/auth') ||
    pathname.startsWith('/forgot') ||
    pathname.startsWith('/users/password') ||
    pathname.startsWith('/users/subdomain')
  )
}

function hydrateApp(preApp) {
  const logs = []

  window.trackPage = trackPage

  initializeTracking()

  if (process.env.NODE_ENV !== 'test') {
    // The current userID is cached in LocalStorage, but we need it in IndexedDB so
    // that the ServiceWorker can access it too.
    sharedStorage.setCurrentUserId((storage.get('currentUser') || {}).id)
    const cachedAuth = storage.get('auth')
    if (cachedAuth) sharedStorage.setAPIAuthToken(cachedAuth.token)
    sharedStorage.setGraphQLUrl(config.graphql_url)
    cacheRegistry.startAutomaticInvalidation()

    // Set up auto-logout detection
    // When auth is removed from another tab (logout), save current URL
    let lastAuthState = Boolean(
      cachedAuth && cachedAuth.authenticated && cachedAuth.token
    )
    let redirectInProgress = false // Gate to prevent double redirects

    // Clear any existing interval to prevent duplicates (dev/HMR scenarios)
    if (window.authCheckInterval) {
      clearInterval(window.authCheckInterval)
    }

    // Also poll for auth changes more frequently to catch logout faster
    const authCheckInterval = setInterval(() => {
      const currentAuth = storage.get('auth')
      const isAuthenticated = Boolean(
        currentAuth && currentAuth.authenticated && currentAuth.token
      )

      if (lastAuthState && !isAuthenticated && !redirectInProgress) {
        // Auth was removed, handle auto-logout
        const currentPath =
          window.location.pathname +
          window.location.search +
          window.location.hash

        if (!isLoginOrPublicPage(currentPath) && currentPath !== '/') {
          redirectInProgress = true // Prevent double redirects
          sessionStorage.setItem('groove-returnTo', currentPath)
          // Auto-logout detected via polling, saved returnTo
          clearInterval(authCheckInterval)
          window.location.replace('/login')
        }
      }
      lastAuthState = isAuthenticated
    }, 1000) // Check every second

    // Store interval ID so it can be cleared if needed
    window.authCheckInterval = authCheckInterval

    // Create storage event handler function
    const storageEventHandler = event => {
      // Check if the groove-auth key changed (it uses groove- namespace prefix)
      if (event.key === 'groove-auth') {
        // Storage event detected for groove-auth

        const newAuth = event.newValue ? JSON.parse(event.newValue) : null
        const wasAuthenticated = lastAuthState
        const isAuthenticated = Boolean(
          newAuth && newAuth.authenticated && newAuth.token
        )

        // Detect logout from another tab
        if (wasAuthenticated && !isAuthenticated && !redirectInProgress) {
          // Save current URL to sessionStorage for this tab
          // Don't save login pages or public pages
          const currentPath =
            window.location.pathname +
            window.location.search +
            window.location.hash

          if (!isLoginOrPublicPage(currentPath) && currentPath !== '/') {
            redirectInProgress = true // Prevent double redirects
            sessionStorage.setItem('groove-returnTo', currentPath)
            // Auto-logout detected, saved returnTo

            // Force immediate redirect to login
            setTimeout(() => {
              // Double-check we're still logged out
              const currentAuth = storage.get('auth')
              if (!currentAuth || !currentAuth.authenticated) {
                // Clear polling interval before redirect
                clearInterval(authCheckInterval)
                // Redirecting to login after auto-logout detection
                window.location.replace('/login')
              }
            }, 100)
          }
        }

        lastAuthState = isAuthenticated
      }
    }

    // Remove any existing storage listener to prevent duplicates during HMR/re-hydration
    if (window.authStorageListener) {
      window.removeEventListener('storage', window.authStorageListener)
    }

    // Store the listener on window and add it
    window.authStorageListener = storageEventHandler
    window.addEventListener('storage', storageEventHandler)
  }

  const version = captureVersion()
  // Use the release sub-path only when the bundle was actually built as a
  // release (RELEASE_VERSION is set in CI for alpha/staging/production). This
  // keeps the dev server working when APP_ENV points at a remote backend,
  // since webpack-dev-server serves assets from `/`, not `/app-release/`.
  const path = `/${process.env.RELEASE_VERSION ? 'app-release' : ''}`

  const app = {
    version,
    // eslint-disable-next-line no-console
    test: () => console.log('Yes this is a test'),
    path,
    salesforce,
    pathPrefix: path === '/' ? '' : path,
    env: config.env,
    config,
    actions: require('./util/builder').actions,
    store,
    storage,
    intersectionObserver: startIntersectionObserver(),
    logs,
    metrics,
    cache: cacheRegistry,
    memory: memory.installProfiling({ store }),
    enableHiddenFeatures: () => {
      storage.set('showSettings', true)
      storage.set('showOnboarding', true)
      storage.set('showAIMessage', true)
      window.location.reload()
    },
    enableShopifyV2LegacyInstallMode: () => {
      // don't delete this even after shopify v2 launch (and don't tell Shopify we have this feature lol)
      // it basically enables the old way of integrating with shopify (where we initiate the oauth dance and not through shopify app store install)
      storage.set('showShopifyV2LegacyInstallMode', true)
      window.location.reload()
    },
    log: message => {
      console.log(message) //  eslint-disable-line no-console
      logs.push(message)
    },
    doShowSnackbar: message => {
      store.dispatch(doShowSnackbar(message))
    },
    doRefreshCounts: () => {
      store.dispatch(doRefreshCounts())
    },
    overlayRoot: document.getElementById('overlays'),
    refreshOnNextPage,
    agentInfo,
    mailboxInfo,
  }

  // use this for features exposed to customers to execute in dev console
  const features = {
    showHiddenSnoozeOptions: () => {
      storage.set('showHiddenSnoozeOptions', true)
    },
  }

  if (config.isAlpha || config.isDevelopment || config.isDocker) {
    fpsExtension(app)
  }

  // register listeners for performance simulator
  registerActionsLogListener()

  global.app = { ...preApp, ...app }
  global.features = features
  global.React = React
  global.ReactDOM = ReactDOM
}

module.exports = {
  hydrateApp,
}
